import React from "react";
import PropTypes from "prop-types";

import TimeAgo from "@/components/TimeAgo";
import { AlertEvent as AlertEventType } from "@/components/proptypes";

import Form from "antd/lib/form";
import Tooltip from "@/components/Tooltip";
import * as Grid from "antd/lib/grid";
import { CheckCircleOutlined, CloseCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';

import Title from "./Title";
import AlertFormItem from "./Alert";
import QueryFormItem from "./Query";

import HorizontalFormItem from "../HorizontalFormItem";
import DynamicComponent from "@/components/DynamicComponent";
import JsonViewInteractive from "@/components/json-view-interactive/JsonViewInteractive";
import {
  DiscordMessages,
  DiscordMessage,
  DiscordEmbed,
  DiscordEmbedFields,
  DiscordEmbedField
} from '@skyra/discord-components-react';
import { Tweet } from 'react-fake-tweet'
import 'react-fake-tweet/dist/index.css'

export default class AlertEventView extends React.Component {
  state = {
    unmuting: false,
    favoriting: false,
    archiving: false,
  };

  archive = () => {
    this.setState({ archiving: true });
    this.props.archive().finally(() => {
      this.setState({ archiving: false });
    });
  };

  favorite = () => {
    this.setState({ favoriting: true });
    this.props.favorite().finally(() => {
      this.setState({ favoriting: false });
    });
  };

  unarchive = () => {
    this.setState({ archiving: true });
    this.props.unarchive().finally(() => {
      this.setState({ archiving: false });
    });
  };

  unfavorite = () => {
    this.setState({ favoriting: true });
    this.props.unfavorite().finally(() => {
      this.setState({ favoriting: false });
    });
  };

  extractLinks = (additionalProperties) => {
    const links = new Set();
    const urlRegex = /(https?:\/\/[^\s)]+)/g; // regex to match plain URLs without trailing parentheses
    const htmlAnchorRegex = /<a\s+(?:[^>]*?\s+)?href="([^"]*)">(.*?)<\/a>/gi; // regex to match HTML anchor tags

    for (const key in additionalProperties) {
      if (typeof additionalProperties[key] === "string") {
        let value = additionalProperties[key];

        // Extract plain URLs
        let urlMatch;
        while ((urlMatch = urlRegex.exec(value)) !== null) {
          links.add(JSON.stringify({ text: urlMatch[0], url: urlMatch[0] }));
        }

        // Extract HTML anchor links
        let htmlMatch;
        while ((htmlMatch = htmlAnchorRegex.exec(value)) !== null) {
          links.add(JSON.stringify({ text: htmlMatch[2], url: htmlMatch[1] }));
        }
      }
    }
    return Array.from(links).map(link => JSON.parse(link));
  };

  extractEthereumData = (additionalProperties) => {
    const ethereumData = {
      transactionIds: [],
      addresses: [],
    };

    // Regex to match Ethereum Transaction IDs (TxIDs)
    const txIdRegex = /\b0x[a-fA-F0-9]{64}\b/g;

    // Regex to match Ethereum Addresses
    const addressRegex = /\b0x[a-fA-F0-9]{40}\b/g;

    for (const key in additionalProperties) {
      if (typeof additionalProperties[key] === "string") {
        let value = additionalProperties[key];

        // Extract Transaction IDs (TxIDs)
        let txIdMatch;
        while ((txIdMatch = txIdRegex.exec(value)) !== null) {
          ethereumData.transactionIds.push(txIdMatch[0]);
        }

        // Extract Addresses
        let addressMatch;
        while ((addressMatch = addressRegex.exec(value)) !== null) {
          ethereumData.addresses.push(addressMatch[0]);
        }
      }
    }
    // Make sure we have unique values
    ethereumData.transactionIds = [...new Set(ethereumData.transactionIds)];
    ethereumData.addresses = [...new Set(ethereumData.addresses)];

    return ethereumData;
  };
  
  
  render() {
    const { alert_event, canEdit, menuButton, onChange, setAlertEventTags, name } = this.props;
    const { alert_type, query, alert, content, created_at, additional_properties } = alert_event;
    function convertMarkdownLinks(text) {
      const markdownLinkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
      return text.replace(markdownLinkRegex, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
    }

    let parsedContent;
    try {
      parsedContent = typeof content === "string" ? JSON.parse(content) : content;
    } catch (e) {
      parsedContent = content;
    }

    let parsedAdditionalProperties;
    try {
      parsedAdditionalProperties = typeof additional_properties === "string" ? JSON.parse(additional_properties) : additional_properties;
    } catch (e) {
      parsedAdditionalProperties = additional_properties;
    }

    const status = parsedAdditionalProperties?.status;
    const message = parsedAdditionalProperties?.message;

    const getStatusIcon = (status) => {
      switch (status) {
        case "OK":
          return <CheckCircleOutlined style={{ color: "green" }} />;
        case "ERROR":
          return <CloseCircleOutlined style={{ color: "red" }} />;
        case "info":
        default:
          return <InfoCircleOutlined style={{ color: "blue" }} />;
      }
    };

    const getMessageIcon = (message) => {
      switch (message) {
        case "SUCCESS":
          return <CheckCircleOutlined style={{ color: "green" }} />;
        case "ERROR":
          return <CloseCircleOutlined style={{ color: "red" }} />;
        case "info":
        default:
          return <InfoCircleOutlined style={{ color: "blue" }} />;
      }
    };

    const linksOfInterest = this.extractLinks(parsedAdditionalProperties);
    const ethereumData = this.extractEthereumData(parsedAdditionalProperties);

    function decimalColortoHextString(decimalColor) {
      if (decimalColor ==="" ||decimalColor < 0 || decimalColor > 16777215) {
        return '#0099ff';
      }
      return '#' + decimalColor.toString(16).padStart(6, '0');
    }

    return (
      <>
        <Title
          name={name}
          alertEvent={alert_event}
          editMode={false}
          tagsExtra={null}
          onChange={onChange}
          canEdit={canEdit}
          setAlertEventTags={setAlertEventTags}
        >
          <DynamicComponent name="AlertEventView.HeaderExtra" alert_event={alert_event} />
          {menuButton}
        </Title>
        <h3>Alert Event Settings</h3>
        <div className="bg-white tiled p-20">
          <Grid.Row type="flex" gutter={16}>
            <Grid.Col xs={24} md={16} className="d-flex">
              <Form className="flex-fill">
                <HorizontalFormItem label="Status">
                  <Tooltip title={status}>
                    {getStatusIcon(status)} {status}
                  </Tooltip>
                </HorizontalFormItem>
                <HorizontalFormItem label="Message">
                  <Tooltip title={message}>
                    {getMessageIcon(message)} {message}
                  </Tooltip>
                </HorizontalFormItem>
                <HorizontalFormItem label="Created At">
                  {Date(created_at).toLocaleString()} (<TimeAgo date={created_at} />)
                </HorizontalFormItem>
                <HorizontalFormItem label="Query">
                  <QueryFormItem query={query} editMode={false} />
                </HorizontalFormItem>
                <HorizontalFormItem label="Alert">
                  <AlertFormItem alert={alert} />
                </HorizontalFormItem>
                <HorizontalFormItem label="Alert Content">
                  {alert_type === "discord_webhook" ? (
                    <DiscordMessages>
                      <DiscordMessage author={alert_event?.destination?.options?.username} avatar={alert_event?.destination?.options?.avatar_url} timestamp={Date(alert_event?.created_at)}>
                      <DiscordEmbed
                        slot="embeds"
                        color={parsedContent["embeds"][0]?.color ? decimalColortoHextString(parsedContent["embeds"][0]?.color) : "#0099ff"}
                        embedTitle={parsedContent["embeds"][0]?.title}
                      >
                        <div dangerouslySetInnerHTML={{ __html: convertMarkdownLinks(parsedContent["embeds"][0]?.description || '') }} />
                        {parsedContent["embeds"][0]?.fields && (
                          <DiscordEmbedFields slot="fields">
                            {parsedContent["embeds"][0].fields.map((field, index) => (
                              <DiscordEmbedField key={index} fieldTitle={field.name} inline={field.inline}>
                                <div dangerouslySetInnerHTML={{ __html: convertMarkdownLinks(field.value) }} />
                              </DiscordEmbedField>
                            ))}
                          </DiscordEmbedFields>
                        )}
                      </DiscordEmbed>
                    </DiscordMessage>
                    </DiscordMessages>
                  ) : alert_event.alert_type === "twitter" ?  (
                    <Tweet
                        config={{
                          user: {
                            avatar: alert_event?.options?.avatar_url,
                            nickname: alert_event?.destination?.options?.account_handle,
                            name: alert_event?.destination?.name,
                          },
                          text: (
                            <span style={{ fontSize: '14px' }}>
                              {parsedContent}
                            </span>
                          ),
                          date: alert_event?.created_at,
                        }}
                      />
                  ):""}
                </HorizontalFormItem>
                <HorizontalFormItem label="Additional Properties">
                  {typeof parsedAdditionalProperties === "object" ? (
                    <JsonViewInteractive value={parsedAdditionalProperties} />
                  ) : (
                    <pre>{String(parsedAdditionalProperties)}</pre>
                  )}
                </HorizontalFormItem>
              </Form>
            </Grid.Col>
            <Grid.Col xs={24} md={8}>
              {linksOfInterest.length > 0 && (
                <div>
                  <h4>Links of Interest</h4>
                  <ul>
                    {linksOfInterest.map((link, index) => (
                      <li key={index}>
                        <a href={link.url} target="_blank" rel="noopener noreferrer">
                          {link.text}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {ethereumData.transactionIds.length > 0 && (
                <div>
                  <h4>Ethereum Transaction IDs</h4>
                  <ul>
                    {ethereumData.transactionIds.map((txId, index) => (
                      <li key={index}>
                        <a href={`https://etherscan.io/tx/${txId}`} target="_blank" rel="noopener noreferrer">
                          {txId}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {ethereumData.addresses.length > 0 && (
                <div>
                  <h4>Ethereum Addresses</h4>
                  <ul>
                    {ethereumData.addresses.map((address, index) => (
                      <li key={index}>
                        <a href={`https://app.zerion.io/${address}`} target="_blank" rel="noopener noreferrer">
                          {address}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Grid.Col>
          </Grid.Row>
        </div>
      </>
    );
  }
}

AlertEventView.propTypes = {
  alert_event: AlertEventType.isRequired,
  canEdit: PropTypes.bool.isRequired,
  menuButton: PropTypes.node.isRequired,
  favorite: PropTypes.func.isRequired,
  unfavorite: PropTypes.func.isRequired,
  archive: PropTypes.func.isRequired,
  unarchive: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  setAlertEventTags: PropTypes.func.isRequired,
};

AlertEventView.defaultProps = {
  queryResult: null,
};
